import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const CART_STORAGE_KEY = 'mh_cart_v1';
const CART_TTL_MS = 30 * 60 * 1000;

export type CartItem = {
  id: string;
  title: string;
  subtitle: string;
  price: number;
  listPrice: number;
  thumbnail?: string;
  addedAt?: string;
};

type CartContextValue = {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (id: string) => void;
  hasItem: (id: string) => boolean;
  clear: () => void;
};

const CartContext = createContext<CartContextValue | null>(null);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const raw = localStorage.getItem(CART_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const now = Date.now();
        return parsed
          .filter((i) => i && typeof i.id === 'string')
          .map((i) => ({
            ...i,
            // Backward compatibility: items saved before addedAt should remain for full TTL window.
            addedAt: i.addedAt || new Date().toISOString(),
          }))
          .filter((i) => {
            const ts = new Date(i.addedAt || 0).getTime();
            return Number.isFinite(ts) && ts > 0 && now - ts <= CART_TTL_MS;
          });
      }
      return [];
    } catch {
      // Ignore malformed local data and start with empty cart.
      return [];
    }
  });
  const hydratedRef = useRef(false);

  useEffect(() => {
    hydratedRef.current = true;
  }, []);

  useEffect(() => {
    if (!hydratedRef.current) return;
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const now = Date.now();
      setItems((prev) =>
        prev.filter((i) => {
          const ts = new Date(i.addedAt || 0).getTime();
          return Number.isFinite(ts) && ts > 0 && now - ts <= CART_TTL_MS;
        }),
      );
    }, 60 * 1000);
    return () => window.clearInterval(timer);
  }, []);

  const addItem = useCallback((item: CartItem) => {
    setItems((prev) => {
      if (prev.some((p) => p.id === item.id)) return prev;
      return [{ ...item, addedAt: new Date().toISOString() }, ...prev];
    });
  }, []);

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const hasItem = useCallback((id: string) => {
    return items.some((item) => item.id === id);
  }, [items]);

  const value = useMemo(
    () => ({ items, addItem, removeItem, hasItem, clear }),
    [items, addItem, removeItem, hasItem, clear],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};
